import React, { useEffect, useState } from 'react';
import _startCase from 'lodash/startCase';
import cx from 'classnames';

import {
    IconDownload,
    IconDownloadedFile,
    IconWrite,
    IconX,
    LabeledValue,
    LoadingSpinner,
    Tooltip,
} from '@trqk/components';
import {
    formatYearAndQuarter,
    formatStatementIdAsYearAndQuarter,
    locale,
} from '@trqk/utils';

import { useEntities, useGapAnalysisTools } from '../../../hooks';
import { DownloadAs } from '../../tooltips';
import { ViewGapAnalysisTable } from './components';
import iconUrl from '../../../assets/svg/insights-empty-gap-analysis.svg';
import { modalWidth, modalHeight } from './constants';

/**
 * Content for a modal that shows a gap analysis report. Since this modal can also open the
 * ManageGapAnalysis modal, both are driven by the context provided by useGapAnalysisTools().
 * @param props - {@link ModalContentProps} with input as a {@link ViewGapAnalysisInput}
 * @returns JSX
 */
export const ViewGapAnalysis = (
    props: ModalContentProps<ViewGapAnalysisInput>,
): JSX.Element => {
    const gapAnalysisId = props?.input?.gapAnalysisId ?? null;
    const {
        context,
        exportGapAnalysis,
        exportingGapAnalysisIds,
        exportingGapAnalysisIdsWithFilename,
        fetchAndSetTrqkGap,
        fetchGapReportAudios,
        openManageGapAnalysis,
        send,
    } = useGapAnalysisTools();
    const { trqkGap, statementId } = context;
    const [loading, setLoading] = useState(true);
    const [gapReportAudios, setGapReportAudios] = useState<GapReportAudio[]>(
        [],
    );

    useEffect(() => {
        if (gapAnalysisId) {
            Promise.all([
                fetchAndSetTrqkGap(gapAnalysisId),
                fetchGapReportAudios(gapAnalysisId),
            ])
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .then(([nextTrqkGap, nextGapReportAudios]) => {
                    setLoading(false);

                    if (nextGapReportAudios !== null) {
                        setGapReportAudios(nextGapReportAudios);
                    }
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    }, [gapAnalysisId]);

    const { selectedEntity } = useEntities();

    const isExporting =
        gapAnalysisId !== null &&
        exportingGapAnalysisIds.includes(gapAnalysisId);

    const isExportingWithFilename =
        gapAnalysisId !== null &&
        exportingGapAnalysisIdsWithFilename.includes(gapAnalysisId);

    const disableDownload = isExporting || gapReportAudios.length < 1;
    const disableDownloadAs =
        isExportingWithFilename || gapReportAudios.length < 1;

    const formattedStatementPeriod = statementId
        ? formatStatementIdAsYearAndQuarter(statementId)
        : null;
    const formattedPerformancePeriod = trqkGap?.performance_period
        ? formatYearAndQuarter(trqkGap?.performance_period)
        : null;

    return (
        <div
            className="bg-white rounded-lg"
            style={{ width: modalWidth, height: modalHeight }}
        >
            <div className="px-10 pt-4 pb-4 border-l-4 rounded-tl-lg border-secondary-500">
                <div className="flex justify-end">
                    <button
                        className="mt-4 mb-2 respond-to-mouse"
                        type="button"
                        onClick={(): void => {
                            props.remove({
                                canceled: true,
                                success: false,
                            });

                            send({
                                type: 'RESTORE_CONTEXT',
                            });
                        }}
                    >
                        <IconX width={20} fillColor="tertiary-400" />
                    </button>
                </div>
                <div className="flex items-end justify-between">
                    <div style={{ maxWidth: modalWidth - 300 }}>
                        <div className="flex items-center ">
                            <img
                                src={iconUrl}
                                width={40}
                                alt="Gap Analysis icon"
                            />
                            <h3 className="ml-2 text-xl leading-6 clamp-2-lines-break-words">
                                {trqkGap?.description ??
                                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE
                                        .DEFAULT_DESCRIPTION}
                            </h3>
                        </div>
                    </div>
                    <div className="space-x-8">
                        <button
                            type="button"
                            onClick={() => {
                                if (trqkGap && gapAnalysisId) {
                                    send({
                                        type: 'SET_TRQK_GAP',
                                        value: {
                                            trqkGap,
                                        },
                                    });

                                    props.remove({
                                        success: true,
                                        canceled: false,
                                    });

                                    setTimeout(() => {
                                        openManageGapAnalysis({
                                            gapAnalysisId,
                                            mode: 'edit',
                                        });
                                    }, 500);
                                }
                            }}
                        >
                            <p className="flex items-center text-sm font-medium underline text-primary-500">
                                <IconWrite
                                    addClasses="mr-1"
                                    width={20}
                                    fillColor="primary-500"
                                />
                                {
                                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE
                                        .BUTTONS.EDIT
                                }
                            </p>
                        </button>
                        <button
                            className={cx({
                                'cursor-default': disableDownload,
                            })}
                            disabled={disableDownload}
                            type="button"
                            onClick={() => {
                                if (gapAnalysisId) {
                                    exportGapAnalysis(gapAnalysisId);
                                }
                            }}
                        >
                            <p className="flex items-center">
                                {!isExporting && (
                                    <IconDownload
                                        width={20}
                                        fillColor={
                                            disableDownload
                                                ? 'tertiary-300'
                                                : 'primary-500'
                                        }
                                    />
                                )}
                                {isExporting && <LoadingSpinner width={20} />}
                                <span
                                    className={cx(
                                        'ml-2 text-sm font-medium underline',
                                        {
                                            'text-primary-500':
                                                !disableDownload,
                                            'text-tertiary-400':
                                                disableDownload,
                                        },
                                    )}
                                >
                                    {
                                        locale.enUS.GAP_ANALYSIS
                                            .REPORT_VIEWER_TABLE.BUTTONS
                                            .DOWNLOAD
                                    }
                                </span>
                            </p>
                        </button>
                        <Tooltip<DownloadAsTooltipMessageProps>
                            addClasses={cx({
                                'cursor-default text-tertiary-300':
                                    isExportingWithFilename,
                            })}
                            disabled={disableDownloadAs}
                            trigger="click"
                            RenderMessage={DownloadAs}
                            renderMessageProps={{
                                handleDownloadAs: (downloadAs: string) => {
                                    if (gapAnalysisId) {
                                        exportGapAnalysis(
                                            gapAnalysisId,
                                            downloadAs,
                                        );
                                    }
                                },
                                fileExtension: '.zip',
                            }}
                            minWidth={450}
                            maxWidth={450}
                            position="left"
                            background="light"
                        >
                            <p className="flex items-center">
                                {!isExportingWithFilename && (
                                    <IconDownloadedFile
                                        width={20}
                                        fillColor={
                                            disableDownloadAs
                                                ? 'tertiary-300'
                                                : 'primary-500'
                                        }
                                    />
                                )}
                                {isExportingWithFilename && (
                                    <LoadingSpinner width={20} />
                                )}
                                <span
                                    className={cx(
                                        'ml-2 text-sm font-medium underline',
                                        {
                                            'text-primary-500':
                                                !disableDownloadAs,
                                            'text-tertiary-400':
                                                disableDownloadAs,
                                        },
                                    )}
                                >
                                    {
                                        locale.enUS.GAP_ANALYSIS
                                            .REPORT_VIEWER_TABLE.BUTTONS
                                            .DOWNLOAD_AS
                                    }
                                </span>
                            </p>
                        </Tooltip>
                    </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4">
                    {selectedEntity && (
                        <LabeledValue
                            label="Entity"
                            value={_startCase(selectedEntity.name)}
                            maxWidth={200}
                        />
                    )}
                    {formattedPerformancePeriod && (
                        <LabeledValue
                            label="Performance Period"
                            value={formattedPerformancePeriod}
                            maxWidth={200}
                        />
                    )}
                    {selectedEntity?.pro && formattedStatementPeriod && (
                        <LabeledValue
                            label="Statement"
                            value={`${selectedEntity.pro} ${formattedStatementPeriod}`}
                            maxWidth={200}
                        />
                    )}
                </div>
            </div>
            {loading && (
                <div className="flex items-center justify-center mt-48">
                    <LoadingSpinner />
                </div>
            )}
            {!loading && (
                <div className="flex flex-col justify-start px-10 pb-10">
                    <ViewGapAnalysisTable
                        gapReportAudios={gapReportAudios}
                        statementId={statementId}
                    />
                </div>
            )}
        </div>
    );
};
